/**
 * Permission Repository Implementation
 * Implements the PermissionRepository interface
 * Coordinates between API calls and mappers
 * Delegates getMyPermissions to shared repository to avoid duplication
 */

import { PermissionRepository } from "../../domain/repositories/PermissionRepository";
import {
  PermissionsMap,
  RolePermissionsEntity,
} from "../../domain/entities/PermissionEntity";
import { PermissionRepositoryImpl as SharedPermissionRepositoryImpl } from "../../../../shared/data/repositories/PermissionRepositoryImpl";
import {
  getUserPermissions,
  getUserPermissionOverrides,
  getUserPermissionsForUI,
  getRoles,
  getRolePermissions,
  createUserPermission,
  deleteUserPermission,
  updateRolePermission,
  getReviewPolicies,
  updateReviewPolicy,
} from "../api/permissionsApi";
import { PermissionMapper } from "../mappers/PermissionMapper";
import {
  PermissionResponseDTO,
  RolePermissionsDTO,
  RolesResponseDTO,
} from "../models/PermissionDTOs";

// Shared repository instance for getMyPermissions delegation
const sharedPermissionRepository = new SharedPermissionRepositoryImpl();

export class PermissionRepositoryImpl implements PermissionRepository {
  async getMyPermissions(): Promise<PermissionsMap> {
    // Delegate to shared repository to avoid duplication
    return await sharedPermissionRepository.getMyPermissions();
  }

  async getUserPermissions(userId: number): Promise<PermissionsMap> {
    const response = (await getUserPermissions(
      userId
    )) as PermissionResponseDTO;
    return PermissionMapper.toPermissionEntity(response);
  }

  async getUserPermissionOverrides(userId: number): Promise<PermissionsMap> {
    const response = (await getUserPermissionOverrides(
      userId
    )) as PermissionResponseDTO;
    return PermissionMapper.toPermissionEntity(response);
  }

  async getUserPermissionsForUI(userId: number): Promise<any> {
    const response = await getUserPermissionsForUI(userId);
    return response.data?.permissions || {};
  }

  async getRoles(): Promise<Array<{ role: string; description: string }>> {
    const response = (await getRoles()) as RolesResponseDTO;
    return response.data || [];
  }

  async getRolePermissions(roleId: number): Promise<RolePermissionsEntity> {
    const response = (await getRolePermissions(roleId)) as RolePermissionsDTO;
    return PermissionMapper.toRolePermissionsEntity(response);
  }

  async createUserPermission(data: {
    user_id: number;
    module_code: string;
    action_code: string;
    is_granted: number;
  }): Promise<void> {
    await createUserPermission(data);
  }

  async updateUserPermission(data: {
    user_id: number;
    module_code: string;
    action_code: string;
    is_granted: number;
  }): Promise<void> {
    // Update is same as create (upsert)
    await createUserPermission(data);
  }

  async deleteUserPermission(id: number): Promise<void> {
    await deleteUserPermission(id);
  }

  async updateRolePermission(
    id: number,
    data: {
      module_code: string;
      action_code: string;
      is_granted: number;
    }
  ): Promise<void> {
    await updateRolePermission(id, data);
  }

  async getReviewPolicies(): Promise<any[]> {
    const response = await getReviewPolicies();
    return response.data || [];
  }

  async updateReviewPolicy(id: number, requiresReview: boolean): Promise<void> {
    await updateReviewPolicy(id, { requires_review: requiresReview });
  }
}
